import type { UserResponse } from '@shared/schemas';
import { all, call, getContext, put, select, takeLatest } from 'redux-saga/effects';

import type { UserDraft } from '../../_shared/domain/user-draft.model';
import { validateDraft } from '../../_shared/domain/user-draft.validation';
import { toEditUserError } from '../domain/edit-user-error';
import type { EditUserGateway } from '../infrastructure/edit-user.gateway';
import { loadRequested, submitRequested } from './edit-user.actions';
import { selectDraft, selectUser } from './edit-user.selectors';
import { editUserActions } from './edit-user.slice';

/**
 * WORKER: hydrate the user being edited. A missing user resolves to a 404-shaped
 * business error so the page can redirect (mirrors the old page's behaviour). All
 * I/O goes through the injected gateway (read from the saga `context`).
 */
function* loadWorker(action: ReturnType<typeof loadRequested>) {
  const gateway: EditUserGateway = yield getContext('editUserGateway');
  yield put(editUserActions.loadStarted());
  try {
    const user = (yield call(gateway.loadUser, action.payload)) as UserResponse | null;
    if (!user) {
      yield put(
        editUserActions.loadFailed({ kind: 'api', message: 'User not found.', status: 404 }),
      );
      return;
    }
    yield put(editUserActions.loadSucceeded(user));
  } catch (e) {
    yield put(editUserActions.loadFailed(toEditUserError(e)));
  }
}

/**
 * WORKER: persist the edited draft. Validates first (pure domain); on failure
 * dispatches a `validation` error and stops without touching the gateway.
 */
function* saveWorker() {
  const user = (yield select(selectUser)) as UserResponse | null;
  if (!user) return;
  const draft = (yield select(selectDraft)) as UserDraft;

  const errors = validateDraft(draft);
  if (Object.keys(errors).length > 0) {
    yield put(editUserActions.saveFailed({ kind: 'validation', fields: errors }));
    return;
  }

  const gateway: EditUserGateway = yield getContext('editUserGateway');
  yield put(editUserActions.saveStarted());
  try {
    yield call(gateway.updateUser, user.id, draft);
    yield put(editUserActions.saveSucceeded());
  } catch (e) {
    yield put(editUserActions.saveFailed(toEditUserError(e)));
  }
}

/** WATCHERS. */
export function* editUserSaga() {
  yield all([
    takeLatest(loadRequested.type, loadWorker),
    takeLatest(submitRequested.type, saveWorker),
  ]);
}
